import { useEffect, type ReactNode } from 'react'
import { LogIn, ShieldAlert } from 'lucide-react'
import { FullPageState, LoadingState } from '@/components/feedback/LoadingState'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useAuthSession } from '@/lib/auth'

function SignInCard({
  title,
  description,
  onSignIn,
  tone = 'neutral',
}: {
  title: string
  description: string
  onSignIn: () => void
  tone?: 'neutral' | 'error'
}) {
  const Icon = tone === 'error' ? ShieldAlert : LogIn
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-muted">
          <Icon className="h-5 w-5 text-muted-foreground" aria-hidden />
        </span>
        <div>
          <h1 className="text-base font-semibold">{title}</h1>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">{description}</p>
        </div>
        <Button variant="primary" onClick={onSignIn}>
          <LogIn className="h-4 w-4" aria-hidden />
          Entrar
        </Button>
      </CardContent>
    </Card>
  )
}

/** Só renderiza o app com sessão válida. No modo oidc, sem sessão → redirect para o Keycloak. */
export function AuthGate({ children }: { children: ReactNode }) {
  const session = useAuthSession()
  const shouldRedirect = session.mode === 'oidc' && session.status === 'unauthenticated'

  useEffect(() => {
    if (shouldRedirect) void session.signIn()
  }, [shouldRedirect, session])

  if (session.status === 'authenticated') return <>{children}</>

  if (session.status === 'loading' || session.status === 'signing_out' || shouldRedirect) {
    const label =
      session.status === 'loading'
        ? 'Verificando sessão…'
        : session.status === 'signing_out'
          ? 'Saindo…'
          : 'Redirecionando para o login…'
    return (
      <FullPageState>
        <LoadingState label={label} />
      </FullPageState>
    )
  }

  if (session.status === 'error') {
    return (
      <FullPageState>
        <SignInCard
          tone="error"
          title="Não foi possível validar a sessão"
          description="O provedor de identidade recusou ou não concluiu o login. Tente entrar novamente."
          onSignIn={() => void session.signIn('/')}
        />
      </FullPageState>
    )
  }

  return (
    <FullPageState>
      <SignInCard
        title="Você saiu do Woctools"
        description="Sessão encerrada. Entre novamente para continuar."
        onSignIn={() => void session.signIn('/')}
      />
    </FullPageState>
  )
}
